import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SetState } from '@unimpaired/utils'
import React, {
  Children,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { AnimateVisible } from '../Animations/AnimateVisible'
import { SliderContent } from '../Animations/Slider/SliderContent'
import { Divider } from '../Dividers/Divider'
import { Modal } from '../Modal/Modal'
import { Todo, TodoProps, TodoListItemVariant } from './Todo'

export type ChecklistItemStatus = 'Pending' | 'Incomplete' | 'Completed'

type ControlledProps = {
  currentTodo: number | undefined
  setCurrentTodo: SetState<number | undefined>
}

type UncontrolledProps = { defaultTodo?: number }

type Props = {
  children:
    | React.ReactElement<TodoProps>
    | (React.ReactElement<TodoProps> | false | null | undefined)[]
  className?: string
  /** Hide the "Step 1 / Step 2 / todo" prefix on every Todo in the
   * list. Useful for flat checklists where the ordering is obvious
   * from position and the numeric prefix just adds visual noise. */
  hideStepText?: boolean
} & (ControlledProps | UncontrolledProps)

const ACCORDION_ANIMATION_MS = 350

const collectTodos = (
  children: Props['children'],
): React.ReactElement<TodoProps>[] =>
  Children.toArray(children).filter(
    (child): child is React.ReactElement<TodoProps> => isValidElement(child),
  )

const variantOf = (todo: TodoProps): TodoListItemVariant =>
  todo.variant ?? 'button'

export const TodoList = (props: Props) => {
  const controlled = 'currentTodo' in props && 'setCurrentTodo' in props

  const [internalIndex, setInternalIndex] = useState<number | undefined>(
    !controlled ? props.defaultTodo : undefined,
  )

  const currentTodoIndex = controlled ? props.currentTodo : internalIndex
  const setCurrentTodoIndex: SetState<number | undefined> = controlled
    ? props.setCurrentTodo
    : setInternalIndex

  const rootRef = useRef<HTMLDivElement>(null)
  const listRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const accordionContentRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [minHeight, setMinHeight] = useState(0)

  const todoElements = collectTodos(props.children)
  const todoProps: TodoProps[] = todoElements.map((child, i) => ({
    ...child.props,
    step: i + 1,
    hideStepText: props.hideStepText ?? child.props.hideStepText,
  }))

  const selectedTodo =
    currentTodoIndex !== undefined ? todoProps[currentTodoIndex] : undefined

  const hasSliderSelected =
    selectedTodo !== undefined && variantOf(selectedTodo) === 'slider'

  const listVariantTodos = todoProps
    .map((todo, i) => ({ todo, index: i }))
    .filter(({ todo }) => variantOf(todo) === 'list')

  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      const current = el.getBoundingClientRect().height
      setMinHeight((prev) => (current > prev ? current : prev))
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (currentTodoIndex === undefined) return
    const item = todoProps[currentTodoIndex]
    if (!item || variantOf(item) !== 'accordion') return
    const timer = setTimeout(() => {
      accordionContentRefs.current[currentTodoIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }, ACCORDION_ANIMATION_MS)
    return () => clearTimeout(timer)
  }, [currentTodoIndex])

  const closeModal = () => setCurrentTodoIndex(undefined)
  const goBackFromSlider = () => setCurrentTodoIndex(undefined)

  const handleItemClick = (index: number, item: TodoProps) => {
    item.onClick?.()
    const variant = variantOf(item)
    if (variant === 'button') return
    if (variant === 'list') {
      setCurrentTodoIndex(index)
      listRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      return
    }
    if (variant === 'accordion' && currentTodoIndex === index) {
      setCurrentTodoIndex(undefined)
      return
    }
    setCurrentTodoIndex(index)
  }

  const checklist = (
    <div className={twMerge('space-y-2', props.className)}>
      {todoProps.map((todo, i) => (
        <React.Fragment key={`${todo.title}-${i}`}>
          <Todo
            {...todo}
            isActive={currentTodoIndex !== undefined && i === currentTodoIndex}
            onClick={() => handleItemClick(i, todo)}
            onClose={() => setCurrentTodoIndex(undefined)}
          />
          {variantOf(todo) === 'accordion' && (
            <AnimateVisible visible={currentTodoIndex === i}>
              <div className='bg-gray-50 rounded-lg'>
                <div
                  className='p-4'
                  ref={(el) => {
                    accordionContentRefs.current[i] = el
                  }}
                  data-testid={`accordion-${i + 1}`}>
                  {todo.children}
                </div>
              </div>
            </AnimateVisible>
          )}
        </React.Fragment>
      ))}
    </div>
  )

  const sliderPanel = selectedTodo && (
    <div className='flex flex-col w-full h-full' data-testid='slider-panel'>
      <div className='flex items-center gap-2 p-2 border-b border-slate-200'>
        <button
          type='button'
          onClick={goBackFromSlider}
          className='flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-100 text-sm font-medium text-slate-700'
          data-testid='slider-back'>
          <FontAwesomeIcon icon={faChevronLeft} />
          Back
        </button>
        <p className='font-medium text-slate-900 truncate'>
          {selectedTodo.title}
        </p>
      </div>
      <div className='flex-1 overflow-y-auto p-4'>{selectedTodo.children}</div>
    </div>
  )

  const hasSliderVariant = todoProps.some((t) => variantOf(t) === 'slider')

  const stepView = hasSliderVariant ? (
    <SliderContent selectedSlide={hasSliderSelected ? 1 : 0}>
      {checklist}
      {sliderPanel}
    </SliderContent>
  ) : (
    checklist
  )

  return (
    <div
      ref={rootRef}
      style={minHeight ? { minHeight } : undefined}
      data-testid='todo-list-root'>
      {stepView}

      <Modal
        isOpen={selectedTodo?.variant === 'modal'}
        onClose={closeModal}
        title={selectedTodo?.title}>
        <div className='p-4'>{selectedTodo?.children}</div>
      </Modal>

      {listVariantTodos.length > 0 && (
        <div className='space-y-4 mt-4'>
          {listVariantTodos.map(({ todo, index }) => (
            <div
              key={`list-${todo.title}-${index}`}
              ref={(el) => {
                listRefs.current[index] = el
              }}
              data-testid={`list-section-${index + 1}`}>
              <Divider text={todo.title} />
              <div className='p-4'>{todo.children}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

TodoList.Todo = Todo
